'use client'

import React, { useEffect, useRef, useState } from 'react'
import AppTooltipWrapper from '@/app/components/atomic/molecules/AppTooltipWrapper'
import { TYPES } from '@/app/components/atomic/AppIcon'
import { withRequiredAsterisk } from '@/app/utils/required-label'
import LoanImportedBadge from '@/app/components/loanComparator/pdfExtract/LoanImportedBadge'
import LoanChatPrefilledBadge from '@/app/components/loanComparator/fields/LoanChatPrefilledBadge'
import type { ChatPrefillMark } from '@/app/lib/guideChat/prefill'

interface LoanFormFieldProps {
    label: string
    tooltip?: {
        title?: string
        text: string
        iconClassName?: string
        iconType?: keyof typeof TYPES
    }
    tooltipId?: string | number
    error?: { message?: string } | undefined
    children: React.ReactNode
    className?: string
    labelClassName?: string
    inline?: boolean
    /** Libellé et contrôle sur une même ligne en desktop, tous les contrôles alignés sur le bord droit de la colonne. */
    row?: 'switch' | 'select'
    labelId?: string
    htmlFor?: string
    forceShowTooltip?: boolean
    imported?: boolean
    chatPrefill?: ChatPrefillMark | null
}

export default function LoanFormField({
    label,
    tooltip,
    tooltipId,
    error,
    children,
    className = '',
    labelClassName = '',
    inline = false,
    row,
    labelId,
    htmlFor,
    forceShowTooltip = false,
    imported = false,
    chatPrefill = null,
}: LoanFormFieldProps) {
    const controlRef = useRef<HTMLDivElement>(null)
    const [editedSinceChatPrefill, setEditedSinceChatPrefill] = useState(false)
    const showChatPrefill = !!chatPrefill && !editedSinceChatPrefill && !imported
    const [isEditingImported, setIsEditingImported] = useState(false)
    const [wasImported, setWasImported] = useState(imported)
    if (imported !== wasImported) {
        setWasImported(imported)
        if (imported) setIsEditingImported(false)
    }
    const isViewingImported = imported && !isEditingImported && !error?.message

    useEffect(() => {
        if (!isEditingImported) return
        controlRef.current?.querySelector<HTMLElement>('input, select, button, [tabindex]')?.focus()
    }, [isEditingImported])

    const enterEditMode = () => setIsEditingImported(true)

    const labelContent = (
        <label
            id={labelId}
            htmlFor={htmlFor}
            className={`min-w-0 flex-1 text-[14px] font-medium text-pale ${labelClassName}`}
        >
            {withRequiredAsterisk(label)}
        </label>
    )

    const importedBadge = isViewingImported ? (
        <LoanImportedBadge />
    ) : showChatPrefill ? (
        <LoanChatPrefilledBadge estimate={chatPrefill.estimate} />
    ) : null

    const containerLayout = row
        ? 'flex flex-col gap-1.5 md:flex-row md:items-center md:gap-4'
        : inline
          ? 'flex flex-col md:flex-row md:items-center gap-1 md:gap-4'
          : ''

    const labelLayout = row ? 'md:min-w-0 md:flex-1' : inline ? 'w-full md:w-7/12 shrink-0' : 'mb-2.5'

    const controlLayout = row
        ? row === 'switch'
            ? 'w-full md:w-auto md:shrink-0'
            : 'w-full md:shrink-0 md:basis-[65%]'
        : inline
          ? 'w-full md:w-5/12 min-w-0'
          : ''

    return (
        <div className={`loan-form-field ${containerLayout} ${className}`}>
            {/* Label + Tooltip */}
            {label && (
                <div className={`flex items-center gap-2 ${row ? 'justify-start' : 'justify-between'} ${labelLayout}`}>
                    {tooltip ? (
                        <AppTooltipWrapper
                            id={tooltipId || label}
                            title={tooltip.title}
                            text={tooltip.text}
                            iconClassName={tooltip.iconClassName || 'fa-info-circle'}
                            iconType={tooltip.iconType || 'regular'}
                            theme="loan"
                            className={`flex min-w-0 items-center gap-1.5 ${row ? 'justify-start' : 'flex-1 justify-between'}`}
                        >
                            {labelContent}
                        </AppTooltipWrapper>
                    ) : (
                        labelContent
                    )}
                    {importedBadge}
                </div>
            )}

            {/* Field + Error */}
            <div
                ref={controlRef}
                data-test={isViewingImported ? 'imported-field-view' : undefined}
                onClickCapture={isViewingImported ? enterEditMode : undefined}
                onFocusCapture={isViewingImported ? enterEditMode : undefined}
                onInputCapture={showChatPrefill ? () => setEditedSinceChatPrefill(true) : undefined}
                onChangeCapture={showChatPrefill ? () => setEditedSinceChatPrefill(true) : undefined}
                className={`${controlLayout} relative ${error?.message ? 'loan-field-has-error' : isViewingImported ? 'loan-field-imported loan-field-imported-view' : ''} ${forceShowTooltip && error?.message ? 'loan-field-first-error' : ''}`}
            >
                {children}
                {!label && importedBadge && (
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                        {importedBadge}
                    </span>
                )}
                {error?.message && (
                    <div
                        className="loan-error-tooltip absolute right-0 z-30 pointer-events-none"
                        style={{
                            bottom: 'calc(100% + 8px)',
                            display: forceShowTooltip ? 'block' : undefined,
                        }}
                    >
                        <div className="relative bg-[#F25B5B] text-white text-[11px] font-bold p-2 rounded whitespace-nowrap">
                            {error.message}
                            <div
                                className="absolute top-full w-0 h-0"
                                style={{
                                    right: '5px',
                                    borderLeft: '6px solid transparent',
                                    borderRight: '6px solid transparent',
                                    borderTop: '6px solid #F25B5B',
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
